import { Controller } from '@hotwired/stimulus'

const INTERSECTION_BUFFER = 45 // Height of <th> + 12px scrollbar buffer

/*
 * Virtual scrollbar that is always visible at the bottom of an owner.
 *
 * The scrollbar structure consists of:
 *
 * ┌─ Scrollbar Parent (fixed width = owner viewport, i.e. 800px) ─┐
 * │  overflow-x: scroll                                           │
 * │  ┌─ Spacer (1px high, width = owner scroll, i.e. 1200px) ─┐   │
 * │  │                                                        │   │
 * │  └──────────────────────────────────────────────────────-─┘   │
 * └─────────────────────────────────────────────────────────────--┘
 *
 * The scroll syncing implementation is relatively simple bidrectional syncing
 * via scroll events.
 *
 * Most of the complexity below is determining
 *  a) when to show the virtual scrollbar
 *      - when owner element is visible in viewport [intersection observer]
 *      - when real scrollbar is NOT visible (bottom of element) [vertical scroll monitoring]
 *  b) keeping the scrollbar width in sync with owner width [resize observer]
 *  c) performance optimizations to reduce expensive DOM calculations [scrollbarVisible]
 */

export default class extends Controller {
    static targets = ['owner', 'scrollbar', 'scrollbarSpacer']

    #resizeObserver = new ResizeObserver(() => this.#toggleFixed())
    #intersectionObserver = null
    #scrollMonitorActive = false
    #scrollbarVisible = false
    #ownerIntersecting = true // Start as true until intersection observer tells us otherwise

    connect() {
        // load CSS only if component is connected
        import('./virtual_scrollbar/scrollbar.scss')

        // Sync scrollbar width when owner is resized
        const observedEl = this.ownerTarget.firstElementChild || this.ownerTarget
        this.#resizeObserver.observe(observedEl)

        // Use Intersection Observer to detect when owner is in viewport
        this.#setupIntersectionObserver()

        // Always start with scroll monitoring - intersection observer will optimize it
        this.#startScrollEventMonitoring()
    }

    disconnect() {
        this.#resizeObserver?.disconnect()
        this.#intersectionObserver?.disconnect()
        this.#stopScrollEventMonitoring()
        this.#stopScrollMonitoring()
    }

    #startScrollMonitoring() {
        if (this.#scrollMonitorActive) return

        this.#scrollMonitorActive = true

        // Add scroll event listeners to sync scroll positions
        this.ownerTarget.addEventListener('scroll', this.#handleOwnerScroll, {
            passive: true,
        })
        this.scrollbarTarget.addEventListener('scroll', this.#handleScrollbarScroll, {
            passive: true,
        })
    }

    #stopScrollMonitoring() {
        if (!this.#scrollMonitorActive) return

        this.#scrollMonitorActive = false

        // Remove scroll event listeners
        this.ownerTarget.removeEventListener('scroll', this.#handleOwnerScroll)
        this.scrollbarTarget.removeEventListener('scroll', this.#handleScrollbarScroll)
    }

    #setupIntersectionObserver() {
        // Instead of checking if the owner is visible on every scroll event we use an intersection observer
        this.#intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    this.#ownerIntersecting = entry.isIntersecting

                    if (!entry.isIntersecting) {
                        // Owner not visible, hide scrollbar immediately
                        this.#hideScrollbar()
                    }
                    // When owner becomes visible, the scroll handler will take care of showing scrollbar
                })
            },
            {
                // Offset slightly so the owner is partially visible before showing the scrollbar
                rootMargin: '50px 0px 50px 0px',
            },
        )

        this.#intersectionObserver.observe(this.ownerTarget)
    }

    #startScrollEventMonitoring() {
        const scrollContainer = document.querySelector('#content') || document
        // Listen to both the container and window scroll events for maximum compatibility
        scrollContainer.addEventListener('scroll', this.#handleOptimizedScroll, { passive: true })
        window.addEventListener('scroll', this.#handleOptimizedScroll, { passive: true })
    }

    #stopScrollEventMonitoring() {
        const scrollContainer = document.querySelector('#content') || document
        scrollContainer.removeEventListener('scroll', this.#handleOptimizedScroll)
        window.removeEventListener('scroll', this.#handleOptimizedScroll)
    }

    #handleOptimizedScroll = () => {
        // Performance optimization: only do expensive calculations when owner is intersecting
        if (!this.#ownerIntersecting) {
            // If owner is not visible and scrollbar is shown, hide it
            if (this.#scrollbarVisible) {
                this.#hideScrollbar()
            }
            return
        }

        if (this._scrollRaf) return
        this._scrollRaf = requestAnimationFrame(() => {
            this.#toggleFixed()
            this._scrollRaf = null
        })
    }

    /**
     * Handle owner scroll events to sync scrollbar position
     */
    #handleOwnerScroll = (event) => {
        if (!this.#scrollMonitorActive) return

        // Prevent circular updates by temporarily storing the source
        if (
            event.target === this.ownerTarget &&
            this.scrollbarTarget.scrollLeft !== this.ownerTarget.scrollLeft
        ) {
            this.scrollbarTarget.scrollLeft = this.ownerTarget.scrollLeft
        }
    }

    /**
     * Handle scrollbar scroll events to sync owner position
     */
    #handleScrollbarScroll = (event) => {
        if (!this.#scrollMonitorActive) return

        // Prevent circular updates by temporarily storing the source
        if (
            event.target === this.scrollbarTarget &&
            this.ownerTarget.scrollLeft !== this.scrollbarTarget.scrollLeft
        ) {
            this.ownerTarget.scrollLeft = this.scrollbarTarget.scrollLeft
        }
    }

    /*
     * Create a "virtual" scrollbar that's only visible when:
     * - The owner has a horizontal scrollbar
     * - The owner is visible in the viewport
     * - The bottom of the owner is not visible (the scrollbar is not at the bottom)
     */
    #showVirtualScrollbar() {
        const ownerRect = this.ownerTarget.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        const hasHorizontalScrollbar = this.ownerTarget.scrollWidth > this.ownerTarget.clientWidth
        const topInView =
            ownerRect.top + INTERSECTION_BUFFER < viewportHeight && ownerRect.bottom > 0
        const bottomIsNotVisible = ownerRect.bottom > viewportHeight

        // Show fixed scrollbar when bottom of owner is not visible
        return hasHorizontalScrollbar && topInView && bottomIsNotVisible
    }

    #hideScrollbar() {
        if (this.#scrollbarVisible) {
            this.#scrollbarVisible = false
            this.scrollbarTarget.classList.add('d-none')
            this.scrollbarTarget.classList.remove('d-block')
            this.#stopScrollMonitoring()
        }
    }

    /**
     * Toggle scrollbar fixed bottom and turn on scroll monitoring
     */
    #toggleFixed() {
        const shouldShow = this.#showVirtualScrollbar()

        if (shouldShow && !this.#scrollbarVisible) {
            // Show scrollbar for the first time
            this.#scrollbarVisible = true

            // Update the virtual scrollbar spacer to match the owner's scroll width
            this.scrollbarSpacerTarget.style.width = `${this.ownerTarget.scrollWidth}px`
            this.scrollbarTarget.scrollLeft = this.ownerTarget.scrollLeft

            // Position virtual scrollbar the same as the owner
            const ownerRect = this.ownerTarget.getBoundingClientRect()
            Object.assign(this.scrollbarTarget.style, {
                left: `${ownerRect.left}px`,
                width: `${ownerRect.width}px`,
            })

            this.scrollbarTarget.classList.remove('d-none')
            this.scrollbarTarget.classList.add('d-block')
            this.#startScrollMonitoring()
        } else if (shouldShow && this.#scrollbarVisible) {
            // Scrollbar already visible, only update position/size if needed
            const ownerRect = this.ownerTarget.getBoundingClientRect()
            const currentLeft = parseInt(this.scrollbarTarget.style.left) || 0
            const currentWidth = parseInt(this.scrollbarTarget.style.width) || 0

            if (
                Math.abs(currentLeft - ownerRect.left) > 1 ||
                Math.abs(currentWidth - ownerRect.width) > 1
            ) {
                Object.assign(this.scrollbarTarget.style, {
                    left: `${ownerRect.left}px`,
                    width: `${ownerRect.width}px`,
                })
            }

            // Sync scroll position if different
            if (this.scrollbarTarget.scrollLeft !== this.ownerTarget.scrollLeft) {
                this.scrollbarTarget.scrollLeft = this.ownerTarget.scrollLeft
            }
        } else if (!shouldShow && this.#scrollbarVisible) {
            this.#hideScrollbar()
        }
    }
}
